"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.portalPcPreferencesSchema = exports.PC_PREFERENCE_FIELDS = exports.pcPreferencesValuesSchema = exports.PC_RESOLUTION_PATTERN = exports.PC_KEYBOARD_LAYOUTS = exports.PC_DEVICE_NAME_MAX = exports.PC_PREFERENCES_MAX_BYTES = exports.PC_PREFERENCES_VERSION = void 0;
exports.pcPreferencesBytes = pcPreferencesBytes;
exports.fitsPcPreferencesCap = fitsPcPreferencesCap;
exports.parsePcPreferences = parsePcPreferences;
exports.pcResolutionText = pcResolutionText;
exports.pcResolutionSize = pcResolutionSize;
exports.isSavablePcDeviceName = isSavablePcDeviceName;
const zod_1 = require("zod");
const events_1 = require("./events");
/**
 * "Remember my settings" (owner, 2026-10-17: a settings tab that is "a full on
 * setting", saved to the customer's account and applied when that customer
 * signs in on any PC). Spec: docs/superpowers/specs/2026-10-17-portal-settings-
 * design.md, "Saved to the account".
 *
 * ONE DOCUMENT PER CUSTOMER, stored whole in `CustomerAccount.pcPreferences`
 * (JSONB) beside the server's own stamp `pcPreferencesUpdatedAt`. The kiosk
 * reads it at sign-in and writes it back, debounced, while the switch is on;
 * the API refuses on write by the schema below and forgives on read by
 * {@link parsePcPreferences}. Staff never see it in v1.
 *
 * PORTABLE VALUES ONLY. Everything here means the same thing on any PC of the
 * venue: a volume, a pointer speed, a key-repeat rate, a refresh-rate wish, a
 * device's friendly NAME. App volumes are not here (the apps differ per
 * sitting), and neither is brightness (it lives in the monitor, and the reset
 * puts each monitor's own back).
 *
 * EVERY VALUE IS OPTIONAL AND NOTHING HAS A DEFAULT. An absent field means
 * "keep the PC's own", which is what makes a half-filled record safe to apply:
 * a customer who only ever touched the mouse gets their mouse and the PC's
 * sound. A `.default()` would break that twice — it would write the default
 * over a value the PC already had, and zod 4 keeps a `.default()` through
 * `.partial()`, so a partial derived from this schema later would silently
 * send it too (the trap cafe.ts, devices.ts and day-passes.ts document).
 *
 * THE VOCABULARY MATCHES THE HOST'S. The ranges are the ones PortalApp clamps
 * to (SettingsRules.cs: scroll 1-100, double-click 200-900 ms, key delay 0-3,
 * key rate 0-31; ShellRules.cs: volume 0-100, pointer 1-20), so a saved value
 * never asks a PC for something it would have to refuse.
 */
/** The envelope's `version`. A record of any other version reads as nothing saved. */
exports.PC_PREFERENCES_VERSION = 1;
/**
 * The whole document, as UTF-8 JSON. Belt and braces: the bounded schema below
 * cannot reach it (its worst case is pinned well under), so this only ever
 * bites a field added later without a thought for size — and a 400 is a far
 * better way to find that out than a row that grows without limit.
 */
exports.PC_PREFERENCES_MAX_BYTES = 2048;
/**
 * A device's friendly name, as Windows reports it ("Headphones (HyperX Cloud
 * III)" is 29). The host already cuts names to 64 before the page ever sees
 * one, so this is room to spare, not a limit a real name meets.
 */
exports.PC_DEVICE_NAME_MAX = 80;
/** The keyboard layouts the venue installs — the host's `pc-keyboard` vocabulary. */
exports.PC_KEYBOARD_LAYOUTS = ['en', 'ar', 'ckb'];
/**
 * "<width>x<height>", the saved resolution wish. Three or four digits each, no
 * leading zero: every mode a venue monitor lists (640x480 up to 7680x4320)
 * and nothing that could be read two ways.
 */
exports.PC_RESOLUTION_PATTERN = /^[1-9][0-9]{2,3}x[1-9][0-9]{2,3}$/;
const whole = (min, max) => zod_1.z.number().int().min(min).max(max);
// eslint-disable-next-line no-control-regex -- deliberate: a device name is one printable line.
const ONE_PRINTABLE_LINE = /^[^\u0000-\u001F\u007F]+$/;
/**
 * A UTF-16 unit that is half of a surrogate pair with no other half beside it:
 * a high half (D800-DBFF) not followed by a low one, or a low half (DC00-DFFF)
 * not preceded by a high one. No `u` flag, so the string is read unit by unit.
 */
const UNPAIRED_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;
/**
 * A saved output or microphone, by its friendly NAME — never an endpoint id,
 * which is per PC and means nothing on the next one. Best effort by design:
 * the page matches it against the names the host lists, and a PC without that
 * device keeps its own.
 *
 * No unpaired surrogate: JSON.stringify writes one as the escape `\ud800`,
 * which Postgres refuses to store (SQLSTATE 22P02). Everything this schema
 * accepts must be storable, so a customer's own bad request is a 400 here and
 * never a 500 at the column. Intact pairs (emoji) are fine.
 */
const deviceName = zod_1.z
    .string()
    .trim()
    .min(1)
    .max(exports.PC_DEVICE_NAME_MAX)
    .regex(ONE_PRINTABLE_LINE)
    .refine((name) => !UNPAIRED_SURROGATE.test(name), {
    message: 'A device name cannot hold half of a surrogate pair.',
});
/**
 * The saved values. Strict: a field this build does not know is refused on
 * write (a typo, or a newer page ahead of this server — which is why the
 * server always deploys first), and dropped on read.
 */
exports.pcPreferencesValuesSchema = zod_1.z
    .object({
    /** Output volume, 0-100, and its mute — the venue bar's own pair. */
    volume: whole(0, 100).optional(),
    muted: zod_1.z.boolean().optional(),
    /** The default microphone's level and mute. */
    micVolume: whole(0, 100).optional(),
    micMuted: zod_1.z.boolean().optional(),
    /** Windows' pointer speed, 1-20 (10 is Windows' own middle). */
    pointer: whole(1, 20).optional(),
    /** "Enhance pointer precision". */
    precision: zod_1.z.boolean().optional(),
    /** Primary and secondary buttons swapped. */
    leftHanded: zod_1.z.boolean().optional(),
    /** Lines per wheel notch (Windows' 3 is the venue's normal). */
    wheelLines: whole(1, 100).optional(),
    /** Double-click time in ms (Windows' 500 is the venue's normal). */
    doubleClickMs: whole(200, 900).optional(),
    /** Key-repeat delay, Windows' 0-3 steps (1 is normal). */
    keyDelay: whole(0, 3).optional(),
    /** Key-repeat rate, Windows' 0-31 steps (31 is normal). */
    keyRate: whole(0, 31).optional(),
    /** The input language the customer typed in. */
    keyboard: zod_1.z.enum(exports.PC_KEYBOARD_LAYOUTS).optional(),
    /** The portal's own language, applied by the page, not the PC. */
    language: events_1.zLocale.optional(),
    /**
     * A refresh-rate wish: "highest", or a number the PC falls back from to
     * the closest it can do at or below it (a 240 wish on a 144 Hz monitor is
     * 144). Never applied unless the monitor lists the mode it lands on.
     */
    refresh: zod_1.z.union([zod_1.z.literal('highest'), whole(24, 1000)]).optional(),
    /** "native", or "<width>x<height>" ({@link PC_RESOLUTION_PATTERN}). */
    resolution: zod_1.z.union([zod_1.z.literal('native'), zod_1.z.string().regex(exports.PC_RESOLUTION_PATTERN)]).optional(),
    /** The output and the microphone the customer picked, by friendly name. */
    outputDevice: deviceName.optional(),
    micDevice: deviceName.optional(),
})
    .strict();
/**
 * Every saved field, in the schema's order. The page walks this to decide
 * what changed and the read parser walks it to keep what it knows; a test
 * pins it to the schema's own keys, so neither can miss a field added later.
 */
exports.PC_PREFERENCE_FIELDS = [
    'volume',
    'muted',
    'micVolume',
    'micMuted',
    'pointer',
    'precision',
    'leftHanded',
    'wheelLines',
    'doubleClickMs',
    'keyDelay',
    'keyRate',
    'keyboard',
    'language',
    'refresh',
    'resolution',
    'outputDevice',
    'micDevice',
];
/**
 * The size of `document` as the database will hold it: UTF-8 bytes of its
 * JSON. Counted by hand rather than through `TextEncoder`, so this module
 * still needs nothing beyond ES2023 in every one of its readers (the API, both
 * web apps). Anything JSON cannot carry — a bigint, a cycle — counts as
 * infinitely large, which every cap refuses.
 */
function pcPreferencesBytes(document) {
    let json;
    try {
        json = JSON.stringify(document);
    }
    catch {
        return Number.POSITIVE_INFINITY;
    }
    if (json === undefined)
        return 0;
    let bytes = 0;
    for (let index = 0; index < json.length; index += 1) {
        const unit = json.charCodeAt(index);
        if (unit < 0x80)
            bytes += 1;
        else if (unit < 0x800)
            bytes += 2;
        else if (unit >= 0xd800 && unit <= 0xdbff && index + 1 < json.length) {
            const next = json.charCodeAt(index + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
                // A surrogate pair is one code point beyond the BMP: four bytes.
                bytes += 4;
                index += 1;
            }
            else {
                bytes += 3;
            }
        }
        else {
            bytes += 3;
        }
    }
    return bytes;
}
/**
 * The size cap's one rule: `document` as UTF-8 JSON is at most
 * {@link PC_PREFERENCES_MAX_BYTES}. The write schema's refinement and the
 * stored-row reader both ask this, so the cap is pinned on it directly: no
 * document the strict schema accepts can reach the cap, so the refinement
 * itself never fires in a test.
 */
function fitsPcPreferencesCap(document) {
    return pcPreferencesBytes(document) <= exports.PC_PREFERENCES_MAX_BYTES;
}
/**
 * The whole document the kiosk saves (`POST /api/portal/profile/pc-preferences`).
 *
 * `remember` is the "Remember my settings" switch itself. Turning it off saves
 * the same values with `remember: false` and deletes nothing; "Forget my saved
 * settings" is its own route and clears the row. A record with `remember:
 * false` is shown with the switch off and applied nowhere.
 *
 * Only three envelope keys, ever: a top-level `set`, `increment` or
 * `decrement` would read as an update OPERATOR to the API's test double.
 */
exports.portalPcPreferencesSchema = zod_1.z
    .object({
    version: zod_1.z.literal(exports.PC_PREFERENCES_VERSION),
    remember: zod_1.z.boolean(),
    values: exports.pcPreferencesValuesSchema,
})
    .strict()
    .refine(fitsPcPreferencesCap, {
    message: `Saved settings are limited to ${exports.PC_PREFERENCES_MAX_BYTES} bytes.`,
});
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * A stored value -> the document, or null for "nothing saved".
 *
 * WRITE REFUSES, READ FORGIVES — the `parsePortalHiddenSections` stance. The
 * column outlives any one build: a row written by a later build that knows a
 * field this one does not must still apply what it can, and a field whose
 * range a later build narrowed must cost that one field, not the customer's
 * whole record. So each field is checked on its own and an invalid one is
 * dropped; unknown keys are dropped.
 *
 * NULL, NOT REPAIRED, when the envelope itself is wrong: not an object (the
 * column's SQL NULL, Prisma's DbNull marker), any version but this one (a
 * later shape this build cannot read safely), a `remember` that is not a
 * boolean (whether to apply anything at all is not something to guess), or a
 * document over the size cap (a row nothing here could have written).
 */
function parsePcPreferences(stored) {
    if (!isRecord(stored))
        return null;
    if (stored.version !== exports.PC_PREFERENCES_VERSION)
        return null;
    if (typeof stored.remember !== 'boolean')
        return null;
    if (!fitsPcPreferencesCap(stored))
        return null;
    const raw = isRecord(stored.values) ? stored.values : {};
    const values = {};
    for (const field of exports.PC_PREFERENCE_FIELDS) {
        if (!Object.prototype.hasOwnProperty.call(raw, field))
            continue;
        const schema = exports.pcPreferencesValuesSchema.shape[field];
        const parsed = schema.safeParse(raw[field]);
        if (parsed.success && parsed.data !== undefined)
            values[field] = parsed.data;
    }
    return {
        version: exports.PC_PREFERENCES_VERSION,
        remember: stored.remember,
        values: values,
    };
}
/**
 * A mode's size as the saved `resolution`, or null when it is not one the
 * schema takes. The page OMITS the field then — a 400 would stop every save
 * of the sitting, and a resolution nobody can save is not worth that.
 */
function pcResolutionText(width, height) {
    if (!Number.isInteger(width) || !Number.isInteger(height))
        return null;
    const text = `${width}x${height}`;
    return exports.PC_RESOLUTION_PATTERN.test(text) ? text : null;
}
/** A saved `resolution` -> its size; null for "native" or anything unreadable. */
function pcResolutionSize(resolution) {
    if (typeof resolution !== 'string' || !exports.PC_RESOLUTION_PATTERN.test(resolution))
        return null;
    const [width, height] = resolution.split('x').map(Number);
    return { width, height };
}
/**
 * Whether a device name Windows reported can be saved EXACTLY as it is.
 *
 * The page omits a name that cannot, rather than trimming or cutting it: the
 * saved name is matched against the names a PC lists, and an altered name
 * could match a different device — or quietly never match at all.
 */
function isSavablePcDeviceName(name) {
    return name === name.trim() && deviceName.safeParse(name).success;
}
//# sourceMappingURL=portal-pc-preferences.js.map